import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSetAtom } from 'jotai';
import {
  currentMetricsMetaSelector,
  supportedMetricsSelector,
  isAppSPIEnabledSelector,
  getUserProfile,
} from '@store/common/selectors';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { CURRENT_PRODUCT_MODE, METRIC_CATEGORY } from '@utils/constants';
import { tid } from '@utils/test';
import { selectedMetricsAtom, allMetricsAtom, metricOrderAtom } from './atoms';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { dimensionsCategory, dimensionsCategoryOrder } from '@utils/dimension/dimensionCategories';
import useMetricCategories from '../common/MetricSelectorPopup/hooks/useMetricCategories';
import { useExcludeMetricCategories } from '../common/MetricSelectorPopup/hooks/useExcludeCategories';
import { useSupportedMetrics } from '../common/MetricSelectorPopup/hooks/useSupportedMetrics';
import useDimensionsCategories from '../dimension-panel-hz/hooks/useDimensionsCategories';
import { ViewerSessionWrapper } from './styled';
import ViewerHeader from './Header';
import {
  getDimensionData,
  getMetricsData,
  getMandatoryDimensions,
  getExtractedMandatory,
  getOrderedMetrics,
} from './utils';
import UserSessionsTable from './Table/TableIndex';
import { get } from 'lodash';
import { getUseCaseLimitation } from '@src/components/common/Dashboard/UseCases/limitation';

function ViewerSessions() {
  const interval = useSelector(chartIntervalSelector);
  const { customer_id } = useSelector(getUserProfile);
  const selectedMetricCardMetric: any = useSelector(selectedMetricSelector);
  const setSelectedMetrics: any = useSetAtom(selectedMetricsAtom);
  const setMetricOrder: any = useSetAtom(metricOrderAtom);
  const isAppSPIEnabled = useSelector(isAppSPIEnabledSelector);
  const setAllMetrics = useSetAtom(allMetricsAtom);
  const mandatoryDimensions = getMandatoryDimensions(customer_id, selectedMetricCardMetric);
  setSelectedMetrics((prev: any) => {
    return [...new Set(prev.concat(mandatoryDimensions))];
  });
  const allDimensions = useSelector(allDimensionsListSelector) as any[];
  let supportedMetrics = useSelector(supportedMetricsSelector) as Record<string, any>;

  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);

  const { usecaseKey } = useSelector(activeDashboardSelector);
  supportedMetrics = useSupportedMetrics(supportedMetrics);

  const excludeMetricCategories = useExcludeMetricCategories();
  if (!isAppSPIEnabled) {
    excludeMetricCategories.push(METRIC_CATEGORY.eco.QOE_AND_KPI_METRICS);
  }

  const metricCategories = useMetricCategories({
    supportedMetrics,
    selectedMetricList: [],
    lockedMetricsSet: new Set(),
    excludeCategories: excludeMetricCategories,
    allMetrics: currentMetricsMeta,
    disableTemplateMetric: get(
      getUseCaseLimitation(usecaseKey),
      ['metrics', 'disableTemplateMetric'],
      false,
    ),
  });

  const dimensionsCategories = useDimensionsCategories({
    allDimensions: allDimensions,
    selectedDimensionsList: [],
    excludeCategories: [],
    hiddenEmptyCategory: !!usecaseKey,
  });
  const currentDimensionsCategoryOrder = dimensionsCategoryOrder[CURRENT_PRODUCT_MODE];
  const dimensionsListCategory = dimensionsCategory[CURRENT_PRODUCT_MODE];

  useEffect(() => {
    const metricsData = getMetricsData(metricCategories);
    const dimensionData = getDimensionData({
      dimensionsCategories,
      currentDimensionsCategoryOrder,
      dimensionsListCategory,
    });
    const extractMandatory = getExtractedMandatory(
      { ...dimensionData, ...metricsData },
      mandatoryDimensions,
    );
    const orderedMetrics = getOrderedMetrics(currentMetricsMeta, allDimensions, customer_id);
    setMetricOrder(orderedMetrics);
    setAllMetrics(extractMandatory);
  }, [
    currentDimensionsCategoryOrder,
    dimensionsCategories,
    dimensionsListCategory,
    mandatoryDimensions,
    metricCategories,
    selectedMetricCardMetric,
    setAllMetrics,
  ]);

  return (
    <ViewerSessionWrapper {...tid('app-viewer-sessions')}>
      <ViewerHeader mandatoryDimensions={mandatoryDimensions} interval={interval} />
      <UserSessionsTable interval={interval} />
    </ViewerSessionWrapper>
  );
}

export default ViewerSessions;
